import React, { FC, useContext, useEffect, useState } from 'react';

import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Switch } from '@mui/material';
import { api, Booking, IBookingDataRetentionRequest, IBookingDataRetentionResponse } from '../../../logic/api';

import { SessionContext } from '../../../utility/useSession';
import { useTranslation } from 'react-i18next';
import TextInputField from '../../../components/editable/EditableField/fields/TextInputField';
import { usePreferences } from '../../../utility/usePreferences';

interface DataRetentionDialogProps {
  booking: Booking | null;
  display: boolean;
  cancel: () => void;
  onDataRetentionConfirm: (booking: Booking | null, error?: string) => void;
}

const DataRetentionDialog: FC<DataRetentionDialogProps> = ({
  booking,
  display,
  cancel,
  onDataRetentionConfirm
}) => {
  const [error, setError] = useState<string>("");

  // Do nothing if not visible or no booking
  if (!display || !booking) {
    return null;
  }

  const handleDataRetentionResponse = (response: IBookingDataRetentionResponse | null) => {
    if (!response) {
      return;
    }

    const { errorMessage } = response;

    if (errorMessage) {
      setError("criticalErrorDataRetention");
    }

    onDataRetentionConfirm(booking, error);
  }

  const dialogStateComponent: JSX.Element = !error ?
    <DataRetentionPromptState booking={booking} cancel={cancel} handleDataRetentionResponse={handleDataRetentionResponse} />
    : <DataRetentionErrorState cancel={cancel} error={error} setError={setError} />;

  return <Dialog maxWidth="md" open={display}>
    {dialogStateComponent}
  </Dialog>;
}


interface DataRetentionErrorStateProps {
  cancel: () => void
  error: string,
  setError: React.Dispatch<React.SetStateAction<string>>
}

const DataRetentionErrorState: FC<DataRetentionErrorStateProps> = ({
  cancel,
  error,
  setError
}) => {
  const { t } = useTranslation();

  return <>
    <DialogTitle>{t("Error")}</DialogTitle>
    <DialogContent>
      {error}
    </DialogContent>
    <DialogActions>
      <Button onClick={() => {
        setError("");
        cancel();
      }}>{t("general.confirm")}</Button>
    </DialogActions>
  </>
}

interface DataRetentionPromptStateProps {
  booking: Booking,
  cancel: () => void,
  handleDataRetentionResponse: (response: IBookingDataRetentionResponse | null) => void,
}

const DataRetentionPromptState: FC<DataRetentionPromptStateProps> = ({
  booking,
  cancel,
  handleDataRetentionResponse
}) => {
  const session = useContext(SessionContext);
  const preferences = usePreferences();

  const [dataRetentionReason, setDataRetentionReason] = useState("");
  const [retainData, setRetainData] = useState<boolean>();
  const [initialRetainData, setInitialRetainData] = useState<boolean>();
  const [isApplying, setIsApplying] = useState(false);
  const { t } = useTranslation();

  const requestDataRetention = api.hooks.booking.retainPIIData();

  useEffect(() => {
    const initial = Boolean(booking.preventObfuscation);
    setRetainData(initial);
    setInitialRetainData(initial);
    setDataRetentionReason("");
  }, [booking]);

  const hasChanged = retainData !== initialRetainData;

  const DataRetention = () => {
    const parameters: IBookingDataRetentionRequest = {
      bookingUUID: booking.uuid,
      reason: dataRetentionReason,
      retainData: Boolean(retainData)
    };

    setIsApplying(true);

    requestDataRetention.send(
      session.current.context,
      parameters,
      (response) => {
        setIsApplying(false);
        handleDataRetentionResponse(response);
      },
    );
  };

  return (
    <div style={{ width: 400 }}>
      <DialogTitle>{t("booking.dataRetention.title")}</DialogTitle>

      <DialogContent>
        {t("booking.dataRetention.prompt", { days: preferences.autoMaskPIIDays })}
        <br />
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ width: 140, paddingBottom: 2 }}>
            {!retainData ? (
              <b>{t("booking.dataRetention.default")}</b>
            ) : (
              <b>{t("booking.dataRetention.off")}</b>
            )}
          </span>
          <span style={{ justifyContent: "flex-end", marginLeft: "auto" }}>
            <Switch
              checked={!retainData}
              value={!retainData}
              onChange={(e, val) => {
                setRetainData(!val);
              }}
            />
          </span>
        </div>

        {hasChanged && (
          <>
            <div style={{ height: 12 }}></div>
            <TextInputField
              autoFocus
              maxWidth={350}
              required
              label={t("booking.dataRetention.reason")}
              onChange={(value) => setDataRetentionReason(value)}
              value={dataRetentionReason}
              maxLength={254}
            />
          </>
        )}

        <div style={{ height: 12 }}></div>
      </DialogContent>

      <DialogActions>
        <Button onClick={cancel}>{t("general.cancel")}</Button>

        <Button
          onClick={DataRetention}
          disabled={
            isApplying ||
            (hasChanged && !dataRetentionReason) ||
            !hasChanged
          }
        >
          <b>{t("general.confirm")}</b>
        </Button>
      </DialogActions>
    </div>
  );
};



export default DataRetentionDialog;
